import { Component, OnInit, ViewChild } from '@angular/core';
import { AlertController, LoadingController } from '@ionic/angular';
import { ReportService } from '../../services/report.service';
import { UserService } from '../../services/user.service';
import { Stylist } from '../../models/stylist/stylist.model';
import { Utilities } from '../../utilities/utilities';
import { SMPickerRange } from '../../components/smr-date-picker/smr-date-picker.component';
import { StylistPickerComponent } from '../../components/stylist-picker/stylist-picker.component';
// import { GoogleAnalytics } from '@awesome-cordova-plugins/google-analytics';
import { ExportTableService } from '../../services/export-table.service';
import { CalendarService } from '../../services/calendar.service';
import { ChartsService } from '../../services/charts.service';
import { Router } from '@angular/router';
import { ChartConfiguration, ChartType, ChartTypeRegistry } from 'chart.js';

interface IRow {
  name: string;
  total: number;
  percentage: number;
}
interface primeNgCol {
  field: string;
  header: string;
}
type MyChartTypeRegistry = {
  bar: ChartType;
  line: ChartType;
  radar: ChartType;
  // ... other chart types
};

@Component({
  selector: 'app-payment-report',
  templateUrl: './payment-report.page.html',
  styleUrls: ['./payment-report.page.scss'],
})
export class PaymentReportPage implements OnInit {
  //@ViewChild(Content) content: Content;

  @ViewChild(StylistPickerComponent)
  stylistPickerComponent: StylistPickerComponent;

  public stats: Array<IRow>;

  public cols: primeNgCol[];

  public stylistID: number;

  public stylist: Stylist;

  public readOnlyStylistPicker: boolean;

  public isLoading: boolean;

  public yearIndicator: boolean = false;

  public xAxisDateChanger: string;

  public errorOccurredWhileLoadingStylistPicker: boolean;

  public errorOccurredWhileLoadingStats: boolean;

  public startDate: Date;

  public endDate: Date;

  public barChartOptions: ChartConfiguration<'bar'>['options'];

  public barChartLabels: string[] = [];

  public barChartType: keyof MyChartTypeRegistry = 'bar';


  public barChartLegend: boolean = true;

  public barChartData: ChartConfiguration<'bar'>['data'] = {
    labels: this.barChartLabels,
    datasets: [
      {
        data: [],
        label: 'Payment Used by Customers',
        borderColor: 'rgba(143, 151, 207, 1)',
        backgroundColor: 'rgba(143, 151, 207, 0.8)',
      },
    ],
  };

  constructor(
    public reportService: ReportService,
    public userService: UserService,
    public alertController: AlertController,
    public loadingCtrl: LoadingController,
    private ExportTableService: ExportTableService,
    private CalendarService: CalendarService,
    private ChartsService: ChartsService,
    private router: Router // ,
  ) // private googleAnalytics: GoogleAnalytics
  {
    this.stats = [];
    this.cols = [
      { field: 'name', header: 'Payment Type' },
      { field: 'total', header: '$ Value' },
      { field: 'percentage', header: 'Percentage' },
    ];

    // this.barChartOptions = ChartsService.barChartOption('', 'top');
    const currency = this.userService.getSalon().getCurrency();
    this.barChartOptions = {
      scales: {
        y: {
          ticks: {
            callback: function (value, index, values) {
              return Utilities.decodeHTML(currency) + value;
            },
          },
        },
      },
    };

    if (this.userService.isLoggedIn()) {
      const user = this.userService.getUser();
      this.stylistID = user.getID();
      this.readOnlyStylistPicker = !(
        user.isAdministrator() || user.getCanViewReports() === 1
      );
    }
  }


  public ngOnInit() {
    // this.googleAnalytics.trackView('PaymentReportPage')
    //   .then(() => {
    //   })
    //   .catch(() => {
    //   });
    if (!this.startDate) {
      let dateFromCache: string = localStorage.getItem('datePickerStartDate');

      if (dateFromCache && Utilities.parseDate(dateFromCache)) {
        this.startDate = Utilities.parseDate(dateFromCache);
      }
    }

    if (!this.endDate) {
      let dateFromCache: string = localStorage.getItem('datePickerEndDate');

      if (dateFromCache && Utilities.parseDate(dateFromCache)) {
        this.endDate = Utilities.parseDate(dateFromCache);
      }
    }
  }

  public ngAfterViewInit() {
    if (this.stylist) {
      this.loadStats(this.stylist, this.startDate, this.endDate);
    }
  }

  public onStylistPickerInit(initResponse: {
    success: boolean;
    stylist: Stylist;
  }) {
    if (initResponse.success) {
      this.onStylistSelect(initResponse.stylist);
    } else {
      this.isLoading = false;
      this.errorOccurredWhileLoadingStylistPicker = true;
    }
  }

  public onStylistSelect(stylist: Stylist) {
    this.isLoading = false;
    if (stylist) {
      this.stylistID = stylist.getID();
      this.stylist = stylist;
      this.loadStats(stylist, this.startDate, this.endDate);
    }
  }

  public onTapToReload() {
    this.isLoading = true;
    this.errorOccurredWhileLoadingStylistPicker = false;
    this.errorOccurredWhileLoadingStats = false;
    this.stylistPickerComponent.ngOnInit();
  }

  public rangeChanged(dateRange: SMPickerRange) {
    this.startDate = dateRange.start;
    this.endDate = dateRange.end;
    this.timeManager(dateRange.start, dateRange.end);
    this.loadStats(this.stylist, this.startDate, this.endDate);
  }

  public loadStats(stylist: Stylist, startDate: Date, endDate: Date) {
    this.timeManager(startDate, endDate);
    this.isLoading = true;
    this.errorOccurredWhileLoadingStats = false;
    this.reportService
      .loadPaymentStats(stylist.id, startDate, endDate)

      .subscribe({
        next: (stats) => {
          const total: number =
            stats.cashTotal +
            stats.debitTotal +
            stats.visaTotal +
            stats.mastercardTotal +
            stats.amexTotal +
            stats.checkTotal +
            stats.giftcardTotal +
            stats.etransferTotal +
            stats.stripeDebitTotal +
            stats.stripeVisaTotal +
            stats.stripeMastercardTotal +
            stats.stripeAmexTotal +
            stats.stripeDinersTotal +
            stats.stripeDiscoverTotal +
            stats.stripeJCBTotal +
            stats.stripePrePaidTotal +
            stats.stripeUnionpayTotal +
            stats.stripeUnkownTotal;

          this.stats = [
            {
              name: 'Cash',
              total: stats.cashTotal,
              percentage:
                total > 0 ? Math.round((stats.cashTotal / total) * 100) : 0,
            },
            {
              name: 'Debit',
              total: stats.debitTotal,
              percentage:
                total > 0 ? Math.round((stats.debitTotal / total) * 100) : 0,
            },
            {
              name: 'Visa',
              total: stats.visaTotal,
              percentage:
                total > 0 ? Math.round((stats.visaTotal / total) * 100) : 0,
            },
            {
              name: 'MasterCard',
              total: stats.mastercardTotal,
              percentage:
                total > 0
                  ? Math.round((stats.mastercardTotal / total) * 100)
                  : 0,
            },
            {
              name: 'AMEX',
              total: stats.amexTotal,
              percentage:
                total > 0 ? Math.round((stats.amexTotal / total) * 100) : 0,
            },
            {
              name: 'Check/Cheque',
              total: stats.checkTotal,
              percentage:
                total > 0 ? Math.round((stats.checkTotal / total) * 100) : 0,
            },
            {
              name: 'Giftcard',
              total: stats.giftcardTotal,
              percentage:
                total > 0 ? Math.round((stats.giftcardTotal / total) * 100) : 0,
            },
            {
              name: 'E-Transfer',
              total: stats.etransferTotal,
              percentage:
                total > 0
                  ? Math.round((stats.etransferTotal / total) * 100)
                  : 0,
            },
            {
              name: 'Stripe Debit',
              total: stats.stripeDebitTotal,
              percentage:
                total > 0
                  ? Math.round((stats.stripeDebitTotal / total) * 100)
                  : 0,
            },
            {
              name: 'Stripe Visa',
              total: stats.stripeVisaTotal,
              percentage:
                total > 0
                  ? Math.round((stats.stripeVisaTotal / total) * 100)
                  : 0,
            },
            {
              name: 'Stripe MasterCard',
              total: stats.stripeMastercardTotal,
              percentage:
                total > 0
                  ? Math.round((stats.stripeMastercardTotal / total) * 100)
                  : 0,
            },
            {
              name: 'Stripe Amex',
              total: stats.stripeAmexTotal,
              percentage:
                total > 0
                  ? Math.round((stats.stripeAmexTotal / total) * 100)
                  : 0,
            },
            {
              name: 'Stripe Diners',
              total: stats.stripeDinersTotal,
              percentage:
                total > 0
                  ? Math.round((stats.stripeDinersTotal / total) * 100)
                  : 0,
            },
            {
              name: 'Stripe Discover',
              total: stats.stripeDiscoverTotal,
              percentage:
                total > 0
                  ? Math.round((stats.stripeDiscoverTotal / total) * 100)
                  : 0,
            },
            {
              name: 'Stripe JCB',
              total: stats.stripeJCBTotal,
              percentage:
                total > 0
                  ? Math.round((stats.stripeJCBTotal / total) * 100)
                  : 0,
            },
            {
              name: 'Stripe Unionpay',
              total: stats.stripeUnionpayTotal,
              percentage:
                total > 0
                  ? Math.round((stats.stripeUnionpayTotal / total) * 100)
                  : 0,
            },
            {
              name: 'Stripe Unknown',
              total: stats.stripeUnkownTotal,
              percentage:
                total > 0
                  ? Math.round((stats.stripeUnkownTotal / total) * 100)
                  : 0,
            },
            {
              name: 'Stripe Prepaid',
              total: stats.stripePrePaidTotal,
              percentage:
                total > 0
                  ? Math.round((stats.stripePrePaidTotal / total) * 100)
                  : 0,
            },
          ];

          this.barChartData.datasets[0].data = [];
          this.barChartLabels = []
          this.stats.forEach((stat) => {
            this.barChartData.datasets[0].data.push(stat.total);
            this.barChartLabels.push(stat.name);
          });
          this.barChartData.labels = this.barChartLabels;
          this.isLoading = false;
          //this.content.resize();
        },
        error: (e) => {
          this.isLoading = false;
          this.errorOccurredWhileLoadingStats = true;
          //this.content.resize();
        },
      });
  }

  public timeManager(dateRangeStart, dateRangeEnd) {
    let resObj = this.CalendarService.timeManager(dateRangeStart, dateRangeEnd);
    this.xAxisDateChanger = resObj.xAxisDateChanger;
    this.yearIndicator = resObj.yearIndicator;
  }

  public summaryTotal() {
    return this.stats
      .map((row) => row['total'])
      .reduce((res, cell) => (res += cell), 0);
  }

  public exportPdf() {
    this.ExportTableService.exportPdf(
      [
        ...this.stats,
        { name: 'Total', total: this.summaryTotal(), percentage: '' },
      ],
      'Payment types',
      this.cols
    );
  }

  public exportExcel() {
    this.ExportTableService.exportExcel(
      [
        ...this.stats,
        { name: 'Total', total: this.summaryTotal(), percentage: '' },
      ],
      'Payment types',
      this.cols.map((col) => col.header)
    );
  }

  public onBack() {
    //this.navCtrl.pop();
    this.router.navigateByUrl('/reports');
  }
}
